import React, { useCallback, useEffect, useRef, useState } from 'react';
import EcosystemFrame, { EcosystemTicker } from '../three/EcosystemFrame';
import FocusDrawer from './FocusDrawer';
import './command.css';

// The one-screen stage: scene in the middle, HUD panels pinned to the
// edges, an activity rail along the bottom, and a focus drawer that slides
// in with the existing deep section for whatever was clicked. The grid is
// sized to the viewport minus the page chrome above it (`chrome` px) so the
// page never scrolls; the drawer scrolls internally.
//
//   panels: [{ id, title, side: 'left'|'right', level?, body, onOpen? }]
//   drawer: { [id]: { title, render: () => node } } — what each panel opens
//   legend: [{ key, label, count }] — tone buckets drawn as chips over the
//           stage; clicking one filters the scene to that bucket
//   inspectorFor(sel): builds the card for a stage selection descriptor
//           ({ kind: 'node'|'host'|'center'|'flow'|'incident'|'anomaly', id })
//           from the CURRENT data, so a pinned card re-derives on every poll
//   defaultInspector: the card shown when nothing is hovered or selected
//   card shape: { title, subtitle, tone, rows: [[k, v]], sparks: [{ label,
//           values, color, last }], sections: [{ title, rows | dots | bars }],
//           issues: [{ level, text }], open: drawerId }
const TONE_WORD = { ok: 'healthy', warn: 'degraded', down: 'down', busy: 'in progress', idle: 'idle', pool: 'pool', off: 'quiet' };

// Tiny inline sparkline: last N buckets, filled area, dot on the last.
function Spark({ values = [], color = 'ok', w = 92, h = 22 }) {
  const n = values.length;
  if (n < 2) return <svg className={`cmd-spark is-${color}`} width={w} height={h} aria-hidden />;
  const max = Math.max(1, ...values);
  const pts = values.map((v, i) => [(i / (n - 1)) * w, h - 1 - (Math.max(0, v) / max) * (h - 3)]);
  const d = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const [lx, ly] = pts[n - 1];
  return (
    <svg className={`cmd-spark is-${color}`} width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden>
      <path d={`${d} L${w},${h} L0,${h} Z`} className="cmd-spark-fill" />
      <path d={d} className="cmd-spark-line" />
      <circle cx={lx} cy={ly} r="1.8" className="cmd-spark-dot" />
    </svg>
  );
}

function Section({ s }) {
  return (
    <div className="cmd-inspector-section">
      <div className="cmd-inspector-section-title">{s.title}</div>
      {s.rows && <div className="cmd-inspector-rows">{s.rows.map(([k, v], i) => <React.Fragment key={`${k}-${i}`}><span>{k}</span><span>{v}</span></React.Fragment>)}</div>}
      {s.dots && <ul className="cmd-inspector-dots">{s.dots.map((d, i) => <li key={i}><i className={`cmd-dot is-${d.level}`} /><span>{d.text}</span></li>)}</ul>}
      {s.bars && (() => { const max = Math.max(1, ...s.bars.map((b) => b.value)); return (
        <div className="cmd-inspector-bars">
          {s.bars.map((b, i) => <div key={`${b.label}-${i}`} className="cmd-inspector-bar"><span>{b.label}</span><i style={{ width: `${Math.max(2, (b.value / max) * 100)}%` }} /><span>{b.text ?? b.value}</span></div>)}
        </div>
      ); })()}
    </div>
  );
}

function Inspector({ card, hint, isDefault = false, pinned = false, onOpen, onClear }) {
  if (!card) return null;
  const tone = card.tone || 'off';
  return (
    <div className={`cmd-inspector is-${tone} ${isDefault ? 'is-default' : ''} ${pinned ? 'is-pinned' : ''}`} aria-live="polite">
      <div className="cmd-inspector-head">
        <strong>{card.title}</strong>
        {TONE_WORD[tone] && <span className={`cmd-inspector-tone is-${tone}`}>{TONE_WORD[tone]}</span>}
        {pinned && <button type="button" className="cmd-inspector-x" onClick={onClear} aria-label="Clear selection">×</button>}
      </div>
      {card.subtitle ? <div className="cmd-inspector-sub">{card.subtitle}</div> : null}
      {card.rows?.length > 0 && (
        <div className="cmd-inspector-rows">
          {card.rows.map(([k, v], i) => <React.Fragment key={`${k}-${i}`}><span>{k}</span><span>{v}</span></React.Fragment>)}
        </div>
      )}
      {card.sparks?.length > 0 && (
        <div className="cmd-inspector-sparks">
          {card.sparks.map((sp) => (
            <div key={sp.label} className="cmd-inspector-spark">
              <Spark values={sp.values} color={sp.color} />
              <span className="cmd-inspector-spark-label">{sp.label}</span>
              <span className={`cmd-inspector-spark-last is-${sp.color}`}>{sp.last}</span>
            </div>
          ))}
        </div>
      )}
      {card.issues?.length > 0 && (
        <ul className="cmd-inspector-issues">
          {card.issues.map((x, i) => <li key={`${x.kind}-${i}`}><i className={`cmd-dot is-${x.level}`} /><span>{x.text}</span></li>)}
        </ul>
      )}
      {pinned && card.sections?.map((s) => <Section key={s.title} s={s} />)}
      <div className="cmd-inspector-foot">
        {hint ? <span className="cmd-inspector-hint">{hint}</span> : null}
        {pinned && card.open && onOpen ? <button type="button" className="cmd-inspector-open" onClick={() => onOpen(card.open)}>Open details →</button> : null}
      </div>
    </div>
  );
}

export default function CommandView({
  data, derive, scene, panels = [], drawer = {}, events = [], caption = null, chrome = 176, className = '', onPick, children,
  telemetry = null, legend = null, defaultInspector = null, inspectorFor = null,
}) {
  const simulated = telemetry?.source === 'sim';
  const [focus, setFocus] = useState(null);
  // Stage feedback: hover previews a card, click pins one (and focuses
  // the stage on it), the legend filter dims everything outside a tone.
  // Hover / selected are descriptors ({ kind, id }); the card itself is
  // rebuilt from the current data on every render so it stays live.
  const [hover, setHover] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState(null);
  const onHover = useCallback((x) => setHover(x || null), []);
  const onSelect = useCallback((x) => setSelected((cur) => (x && cur && cur.kind === x.kind && cur.id === x.id ? null : x)), []);
  useEffect(() => { if (filter && legend && !legend.some((l) => l.key === filter && l.count > 0)) setFilter(null); }, [legend, filter]);
  // Hovering the selected object itself keeps the pinned card (no flicker to a preview).
  const sameAsSelected = !!(hover && selected && hover.kind === selected.kind && hover.id === selected.id);
  const hoverCard = hover && !sameAsSelected && inspectorFor ? inspectorFor(hover) : null;
  const selectedCard = selected && inspectorFor ? inspectorFor(selected) : null;
  // The selected object left the stage (instance deleted, incident closed) → drop the pin.
  useEffect(() => { if (selected && inspectorFor && !inspectorFor(selected)) setSelected(null); }, [selected, inspectorFor]);
  const card = hoverCard || selectedCard || defaultInspector;
  const pinned = !hoverCard && !!selectedCard;
  // The grid is viewport height minus whatever sits above it. Measure the
  // real offset (header rows, banners, help text vary per page) instead of
  // trusting the `chrome` estimate; re-measure on resize.
  const rootRef = useRef(null);
  const [top, setTop] = useState(null);
  useEffect(() => {
    const measure = () => { if (rootRef.current) setTop(Math.round(rootRef.current.getBoundingClientRect().top + window.scrollY)); };
    measure();
    const t = setTimeout(measure, 300);
    window.addEventListener('resize', measure);
    return () => { clearTimeout(t); window.removeEventListener('resize', measure); };
  }, [chrome, panels.length]);
  // Deep links (#ap-health …) open the matching drawer entry in command mode.
  useEffect(() => {
    const apply = () => { const id = window.location.hash.replace('#', ''); if (id && drawer[id]) setFocus(id); };
    apply();
    window.addEventListener('hashchange', apply);
    return () => window.removeEventListener('hashchange', apply);
  }, [drawer]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Escape peels one layer: drawer first, then the pinned selection.
      setFocus((f) => { if (f) return null; setSelected(null); return f; });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const left = panels.filter((p) => p.side !== 'right');
  const right = panels.filter((p) => p.side === 'right');
  const panel = (p) => (
    <button
      key={p.id}
      type="button"
      className={`cmd-panel is-${p.level || 'off'} ${p.wide ? 'is-wide' : ''}`}
      onClick={() => { if (p.onOpen) p.onOpen(); else if (drawer[p.id]) setFocus(p.id); }}
      aria-label={`Open ${p.title}`}
    >
      <span className="cmd-panel-title"><i className={`cmd-dot is-${p.level || 'off'}`} />{p.title}</span>
      <span className="cmd-panel-body">{p.body}</span>
    </button>
  );

  return (
    <div ref={rootRef} className={`cmd-view ${className} ${simulated ? 'is-simulated' : ''}`} style={{ '--cmd-chrome': `${(top ?? chrome) + 12}px` }}>
      <div className="cmd-stage" onPointerLeave={() => setHover(null)}>
        <EcosystemFrame data={data} derive={derive} scene={scene} fill caption={false} ticker={false} telemetry={telemetry} onHover={inspectorFor ? onHover : undefined} onSelect={inspectorFor ? onSelect : undefined} selected={inspectorFor ? selected : null} filter={filter} onPick={(id) => { if (onPick) onPick(id); else if (drawer[id]) setFocus(id); }} />
        {legend && data && (
          <div className="cmd-legend" role="group" aria-label="Filter the stage by state">
            {legend.map((l) => (
              <button
                key={l.key}
                type="button"
                className={`cmd-legend-chip ${filter === l.key ? 'is-active' : ''} ${l.count ? '' : 'is-empty'}`}
                onClick={() => setFilter((f) => (f === l.key ? null : l.key))}
                aria-pressed={filter === l.key}
                title={filter === l.key ? 'Show everything' : `Show only ${l.label}`}
              >
                <i className={`cmd-legend-dot is-${l.key}`} /><strong>{l.count}</strong>{l.label}
              </button>
            ))}
          </div>
        )}
        {simulated && <div className="cmd-watermark" aria-label="Simulated data">SIMULATED{telemetry.scenario && telemetry.scenario !== 'calm' ? ` · ${telemetry.scenario}` : ''}</div>}
        {data && card && (
          <Inspector
            card={card}
            isDefault={!hoverCard && !selectedCard}
            pinned={pinned}
            onOpen={(id) => { if (onPick && !drawer[id]) onPick(id); else if (drawer[id]) setFocus(id); }}
            onClear={() => setSelected(null)}
            hint={hoverCard ? 'click to pin · double-click for details' : pinned ? 'pinned · updates live · Esc clears' : filter ? `showing only ${TONE_WORD[filter] || filter} · click the chip again for everything` : 'click anything on the stage for its live card'}
          />
        )}
        {caption && <div className="cmd-caption">{simulated ? 'simulated · ' : ''}{caption}</div>}
      </div>
      <div className="cmd-col cmd-col-left">{left.map(panel)}</div>
      <div className="cmd-col cmd-col-right">{right.map(panel)}</div>
      <div className="cmd-rail">
        {children}
        <EcosystemTicker events={events} limit={5} />
      </div>
      <FocusDrawer open={!!focus} title={focus ? drawer[focus]?.title : ''} onClose={() => { setFocus(null); if (window.location.hash) window.history.replaceState(null, '', window.location.pathname + window.location.search); }}>
        {focus && drawer[focus]?.render?.()}
      </FocusDrawer>
    </div>
  );
}
